import {
  AnalyzeResult,
  EntityListComponent,
  PrepareResult,
} from '@backstage/plugin-catalog-import';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { partition } from 'lodash';
import { useCallback, useState } from 'react';
import { BackButton, NextButton } from '../Buttons';

type Props = {
  analyzeResult: Extract<AnalyzeResult, { type: 'locations' }>;
  prepareResult?: PrepareResult;
  onPrepare: (result: PrepareResult) => void;
  onGoBack?: () => void;
};

/**
 * A form that lets a user select one of a list of locations to import
 *
 * @param analyzeResult - the result of the analysis
 * @param prepareResult - the selectected locations from a previous step
 * @param onPrepare - called after the selection
 * @param onGoBack - called to go back to the previous step
 */
export const StepPrepareSelectLocations = ({
  analyzeResult,
  prepareResult,
  onPrepare,
  onGoBack,
}: Props) => {
  const [selectedUrls, setSelectedUrls] = useState<string[]>(
    prepareResult?.locations.map(l => l.target) || [],
  );

  const [existingLocations, locations] = partition(
    analyzeResult?.locations,
    l => l.exists,
  );

  const handleResult = useCallback(async () => {
    onPrepare({
      type: 'locations',
      locations: locations.filter((l: any) => selectedUrls.includes(l.target)),
    });
  }, [locations, onPrepare, selectedUrls]);

  const onItemClick = (url: string) => {
    setSelectedUrls(urls =>
      urls.includes(url) ? urls.filter(u => u !== url) : urls.concat(url),
    );
  };

  const onSelectAll = () => {
    setSelectedUrls(urls =>
      urls.length < locations.length ? locations.map(l => l.target) : [],
    );
  };

  return (
    <>
      {locations.length > 0 && (
        <>
          <Typography>
            Select one or more locations that are present in your git
            repository:
          </Typography>
          <EntityListComponent
            firstListItem={
              <ListItem dense button onClick={onSelectAll}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedUrls.length === locations.length}
                    indeterminate={
                      selectedUrls.length > 0 &&
                      selectedUrls.length < locations.length
                    }
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary="Select All" />
              </ListItem>
            }
            onItemClick={onItemClick}
            locations={locations}
            locationListItemIcon={target => (
              <Checkbox
                edge="start"
                checked={selectedUrls.includes(target)}
                tabIndex={-1}
                disableRipple
              />
            )}
            collapsed
          />
        </>
      )}

      {existingLocations.length > 0 && (
        <>
          <Typography>These locations already exist in the catalog:</Typography>
          <EntityListComponent
            locations={existingLocations}
            locationListItemIcon={() => <LocationOnIcon />}
            withLinks
            collapsed
          />
        </>
      )}

      <Grid container spacing={0}>
        {onGoBack && <BackButton onClick={onGoBack} />}
        <NextButton disabled={selectedUrls.length === 0} onClick={handleResult}>
          Review
        </NextButton>
      </Grid>
    </>
  );
};
